import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "@mui/material";

import { useGraphColors } from "@core";

import { BaseTimeSeriesChart, TimeSeriesData, YAxisConfig } from "../charts/BaseTimeSeriesChart";
import type { DateRange, GraphSettings, Resolution, TypedChartSeries } from "../shared/types";

// Series types for optimization charts
export enum OptimizationSeriesType {
  MEASURED_OFFSET = "measured_offset",
  SUPPLY_TEMP = "supply_temp",
  REQUESTED_OFFSET = "requested_offset",
  // PREDICTED_OFFSET = "predicted_offset",
}

type OptimizationChartContainerProps = {
  data: TypedChartSeries<OptimizationSeriesType>[];
  isLoading?: boolean;
  chartHeight?: number;
  dateRange?: DateRange;
  resolution?: Resolution;
  graphSettings?: GraphSettings;
  yAxisLayout?: "stacked" | "side-by-side"; // New prop for y-axis layout
};

/**
 * OptimizationChartContainer - Container for optimization time series charts
 *
 * This component handles the specific data formatting and configuration for the optimization chart
 */
export const OptimizationChartContainer: React.FC<OptimizationChartContainerProps> = ({
  data,
  isLoading = false,
  chartHeight,
  dateRange,
  resolution,
  graphSettings,
  yAxisLayout = "side-by-side", // Default to stacked for backward compatibility
}) => {
  const { t } = useTranslation(["propai_properties", "_common"]);
  const theme = useTheme();
  const graphColors = useGraphColors();

  // Configuration for each series
  const OPTIMIZATION_SERIES_CONFIG = useMemo((): Record<
    OptimizationSeriesType,
    {
      name: string;
      unit: string;
      color: string;
      yAxisIndex: number;
      visible: boolean;
    }
  > => {
    return {
      [OptimizationSeriesType.MEASURED_OFFSET]: {
        name: t("text_reported_offset"),
        unit: " °C",
        color: graphColors[0],
        yAxisIndex: 0,
        visible: true,
      },
      [OptimizationSeriesType.SUPPLY_TEMP]: {
        name: t("text_radiator_supply_temperature"),
        unit: " °C",
        color: graphColors[1],
        yAxisIndex: 1,
        visible: true,
      },
      [OptimizationSeriesType.REQUESTED_OFFSET]: {
        name: `${t("text_requested_offset")}`,
        unit: " °C",
        color: graphColors[2],
        yAxisIndex: 0,
        visible: true,
      },
    };
  }, [t, graphColors]);

  // Y-axis configurations
  const Y_AXIS_CONFIGS = useMemo((): YAxisConfig[] => {
    return [
      {
        title: { text: `${t("text_offset_temperature")} (°C)` },
        showLastLabel: true,
        showFirstLabel: true,
        softMin: -2,
        softMax: 2,
        plotLines: [
          {
            value: 0,
            width: 3,
            color: theme.palette.divider,
            zIndex: 1,
          },
        ],
      },
      {
        title: { text: `${t("text_supply_temperature")} (°C)` },
        showLastLabel: true,
        showFirstLabel: true,
        softMin: 0,
        softMax: 40,
      },
      {
        title: { text: `${t("text_offset_temperature")} (°C)` },
        showLastLabel: true,
        showFirstLabel: true,
        softMin: -2,
        softMax: 2,
      },
    ];
  }, [t, theme]);
  // Transform optimization data to time series format
  const series = useMemo((): TimeSeriesData[] => {
    const result: TimeSeriesData[] = [];

    data.forEach((seriesData) => {
      const config = OPTIMIZATION_SERIES_CONFIG[seriesData.type];
      if (config && seriesData.data.length > 0) {
        result.push({
          uid: seriesData.type,
          name: config.name,
          data: seriesData.data,
          unit: config.unit,
          color: config.color,
          yAxisIndex: config.yAxisIndex,
          visible: config.visible,
        });
      }
    });

    return result;
  }, [data, OPTIMIZATION_SERIES_CONFIG]);

  return (
    <BaseTimeSeriesChart
      series={series}
      yAxisConfigs={Y_AXIS_CONFIGS}
      chartHeight={chartHeight}
      dateRange={dateRange}
      resolution={resolution}
      graphSettings={graphSettings}
      isLoading={isLoading}
      allowSeriesToggle={true}
      yAxisLayout={yAxisLayout} // Pass the new prop
    />
  );
};
